/**
 * The type-specific half of a `classify` agent's settings page: how sure it
 * must be before it proposes, whether it may re-propose on items a person
 * already classified, and a try-it box that runs the agent on a sample item
 * and writes nothing. Stored in agent_settings.behavior_config.
 *
 * The Air Traffic Controller and every custom agent made on its behavior show
 * this same panel; the rest of the page is the template's common switches.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Callout, Select, Switch, TextArea, TextInput } from '../../ds';
import { resolveClassifyConfig } from '../../../lib/classifyRunner';
import { runAtcAgent, type AtcResult } from '../../../lib/llmAirTrafficControllerService';
import type { ClassifyBehaviorConfig } from '../../../lib/ai/agentDefinitions';
import type { BehaviorSettingsPanelProps } from './types';

export function ClassifySettings({ settings, isSaving, saveBehaviorConfig }: BehaviorSettingsPanelProps) {
  const behavior = resolveClassifyConfig(settings);
  const saveBehavior = (patch: Partial<ClassifyBehaviorConfig>) => saveBehaviorConfig({ ...behavior, ...patch });

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<AtcResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tryIt = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const reply = await runAtcAgent({
        title,
        description,
        referenceDocs: settings?.referenceDocs ?? [],
        model: settings?.model,
        systemPrompt: settings?.systemPrompt ?? null,
        modelParams: settings?.modelParams ?? null,
      });
      setResult(reply.result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The agent could not run.');
    }
    setRunning(false);
  };

  return (
    <div className="card space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-[var(--text-pri)]">How it classifies</h3>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">The settings that belong to this type of agent.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-xs font-medium text-[var(--text-sec)] mb-1">How sure it must be before it proposes</p>
          <Select
            value={behavior.minConfidence}
            disabled={isSaving}
            onChange={(e) => void saveBehavior({ minConfidence: e.target.value as ClassifyBehaviorConfig['minConfidence'] })}
            className="w-full"
          >
            <option value="low">Any confidence — propose every time</option>
            <option value="medium">Medium or high</option>
            <option value="high">High only</option>
          </Select>
          <p className="text-xs text-[var(--text-ter)] mt-1">Below this, it records why it held back and proposes nothing.</p>
        </div>
        <div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-medium text-[var(--text-sec)]">Re-propose on classified items</p>
            <Switch
              checked={behavior.includeClassified}
              disabled={isSaving}
              aria-label="Re-propose on classified items"
              onChange={() => void saveBehavior({ includeClassified: !behavior.includeClassified })}
            />
          </div>
          <p className="text-xs text-[var(--text-ter)] mt-1">
            Off: runs over many items skip any item a person already classified. A person can still ask it about one item.
          </p>
        </div>
      </div>

      <div className="space-y-2 border-t border-[var(--rule)] pt-4">
        <p className="text-xs font-medium text-[var(--text-sec)]">Try it on a sample item</p>
        <p className="text-xs text-[var(--text-ter)]">Uses this page's model, prompt and reference documents. Nothing is saved.</p>
        <TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" aria-label="Sample title" />
        <TextArea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" rows={3} aria-label="Sample description" />
        <Button size="sm" variant="secondary" disabled={running || !title.trim()} onClick={() => void tryIt()}>
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {running ? 'Running…' : 'Try it'}
        </Button>
        {error && <Callout tone="coral">{error}</Callout>}
        {result && (
          <div className="border border-[var(--rule)] p-3 text-sm space-y-1">
            <p className="text-[var(--text-pri)]">
              <span className="font-medium capitalize">{result.suggestedCategory}</span> · altitude {result.altitudeLevel} · {result.executionProfile} · {result.confidence} confidence
            </p>
            <p className="text-xs text-[var(--text-sec)]">
              {result.questionResponses.map((r) => `${r.dimension} ${r.selectedValue}`).join(' · ')}
            </p>
            {result.rationale && <p className="text-xs text-[var(--text-sec)]">{result.rationale}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
